import { Badge, Tooltip } from "@mantine/core";
import { IconQuestionMark } from "@tabler/icons-react";
import { useClient } from "#/hook/useClient";
import { formatDateTime } from "#/utils/dateTimeUtils";
import type { TargetCheck, TargetStatus } from "./prvUtils";
import { usePrvTranslation } from "./usePrvTranslation";

const BADGE_COLOR: Record<TargetStatus, string> = {
  met: "green",
  below: "red",
  overflow: "orange",
};

interface Props {
  /** Null when no rotation target is defined for the pair. */
  check: TargetCheck | null;
  /** End of the latest interval: the last run's start. */
  asOf: string;
}

/**
 * The rotation target of one sub-client × category as one coloured badge:
 * green within ±10 % of the target, red below, orange above (overflow), grey
 * no target. The words stay out of the badge; the hover carries the full
 * explanation with the real numbers.
 */
const PrvTargetBadge = ({ check, asOf }: Props) => {
  const t = usePrvTranslation();
  const { client } = useClient();

  const lines = check
    ? [
        t("clients.prv.results.targetTip.definition", { count: check.count, days: check.days }),
        t("clients.prv.results.targetTip.arrived", {
          days: check.days,
          arrived: check.arrived,
          asOf: formatDateTime(asOf, client),
        }),
        check.status === "met"
          ? t("clients.prv.results.targetTip.met")
          : check.status === "below"
            ? t("clients.prv.results.targetTip.below", { short: check.count - check.arrived })
            : t("clients.prv.results.targetTip.overflow", { over: check.arrived - check.count }),
        t("clients.prv.results.targetTip.source"),
      ]
    : [t("clients.prv.results.targetTip.none")];

  return (
    <Tooltip
      withArrow
      multiline
      maw={320}
      label={
        <div className="flex flex-col gap-1 text-xs">
          {lines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </div>
      }
    >
      <Badge
        variant="light"
        color={check ? BADGE_COLOR[check.status] : "gray"}
        size="sm"
        className="cursor-help normal-case"
        rightSection={<IconQuestionMark size={11} stroke={2.5} />}
      >
        {check
          ? t("clients.prv.results.cards.targetBadge", {
              arrived: check.arrived,
              count: check.count,
              days: check.days,
            })
          : t("clients.prv.results.cards.targetNone")}
      </Badge>
    </Tooltip>
  );
};

export default PrvTargetBadge;
